import * as React from 'react';
import { Link } from 'react-router-dom';
import { BASELINE_SEARCH_SORT_RANK, JobSearchesSourcingStats, SearchResultsViewType } from 'shared/models/search';

import { getStatusLabel } from '../common/searches';
import { timeRelativeDay } from '../common/timestamp';
import { User } from '../sfc/user';
import { Job, Search, SearchStatus } from '../state';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';
import { JobSearchesTitle } from './job-searches-title';
import { SearchAutoSendToggle } from './search-auto-send-toggle';

type Column =
    | 'title'
    | 'status'
    | 'ready'
    | 'lowScore'
    | 'noScore'
    | 'inCooldown'
    | 'added'
    | 'alreadyAdded'
    | 'blocked'
    | 'findingEmail'
    | 'expiredProfile'
    | 'rejected'
    | 'totalNew'
    | 'created'
    | 'owner'
    | 'createdBy';

interface JobSearchesStatsProps {
    displayDetail: boolean;
    searches: Search[];
    job: Job;
    searchStats: JobSearchesSourcingStats[];
    onDisplaySearchInfo: (searchId: string) => void;
    onCloneSearch: (data: Search) => void;
    onUpdateSearch: (id: string, updates: Partial<Search>, refetchSearches: boolean) => void;
    onDeleteSearch: (data: Search) => void;
}

export const JobSearchesStats: React.FC<JobSearchesStatsProps> = ({
    displayDetail,
    searches,
    job,
    searchStats,
    onCloneSearch,
    onUpdateSearch,
    onDeleteSearch
}) => {
    const tableContainerRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        const table = tableContainerRef.current;
        if (table) {
            table.scrollTo({
                behavior: 'smooth',
                top: table.scrollHeight
            });
        }
    }, [searches, searchStats, displayDetail]);

    const getColumnLabel = (columnId: Column): string => {
        const labels: Record<Column, string> = {
            added: 'Added',
            alreadyAdded: 'Already Added',
            blocked: 'Blocked',
            created: 'Created',
            createdBy: 'Created By',
            expiredProfile: 'Expired Profile',
            findingEmail: 'Finding Email',
            inCooldown: 'In Cooldown',
            lowScore: 'Low Score',
            noScore: 'No Score',
            owner: 'Owner',
            ready: 'Ready',
            rejected: 'Rejected',
            status: 'Status',
            title: 'Title',
            totalNew: 'Total New'
        };
        return labels[columnId];
    };

    const createColumn = (id: Column): DataPanelColumn<Column> => ({
        id,
        label: getColumnLabel(id)
    });

    const baseColumnIds: Column[] = [
        'title',
        'status',
        'ready',
        'lowScore',
        'noScore',
        'inCooldown',
        'totalNew',
        'created',
        'owner'
    ];

    const detailColumnIds: Column[] = [
        'title',
        'status',
        'ready',
        'lowScore',
        'noScore',
        'inCooldown',
        'added',
        'alreadyAdded',
        'blocked',
        'findingEmail',
        'expiredProfile',
        'rejected',
        'totalNew',
        'created',
        'owner',
        'createdBy'
    ];

    const sortableColumns = [
        'status',
        'ready',
        'lowScore',
        'noScore',
        'inCooldown',
        'added',
        'alreadyAdded',
        'blocked',
        'findingEmail',
        'expiredProfile',
        'rejected',
        'totalNew',
        'created',
        'owner',
        'createdBy'
    ];

    const filterableColumns = ['status'];

    const columns = (displayDetail ? detailColumnIds : baseColumnIds).map(createColumn);

    const filteredSearches = React.useMemo(() => searches.filter((s) => s.status !== SearchStatus.Initial), [searches]);

    const getStatCount = (search: Search, statKey: keyof Omit<JobSearchesSourcingStats, 'id'>): CellValue => {
        if (search.status !== SearchStatus.Active) {
            return '-';
        }
        const stats = searchStats.find((st) => st.id === search.id);
        return stats?.[statKey] ?? '-';
    };

    const getCellValue =
        (column: Column) =>
        (search: Search): CellValue => {
            switch (column) {
                case 'title':
                    return search.id;
                case 'status':
                    return getStatusLabel(search, search.status);
                case 'ready':
                    return getStatCount(search, 'ready');
                case 'lowScore':
                    return getStatCount(search, 'lowScore');
                case 'noScore':
                    return getStatCount(search, 'noScore');
                case 'inCooldown':
                    return getStatCount(search, 'inCooldown');
                case 'added':
                    return getStatCount(search, 'added');
                case 'alreadyAdded':
                    return getStatCount(search, 'alreadyAdded');
                case 'blocked':
                    return getStatCount(search, 'blocked');
                case 'findingEmail':
                    return getStatCount(search, 'findingEmail');
                case 'expiredProfile':
                    return getStatCount(search, 'expiredProfile');
                case 'rejected':
                    return getStatCount(search, 'rejected');
                case 'totalNew':
                    return getStatCount(search, 'totalNew');
                case 'created':
                    return search.createdAt;
                case 'owner':
                    return search.userId;
                case 'createdBy':
                    return search.createdBy;
            }
        };

    const renderClickableCount = (
        value: CellValue,
        search: Search,
        resultsType: SearchResultsViewType
    ): JSX.Element | string => {
        if (search.status !== SearchStatus.Active || value === '-') {
            return '-';
        }
        return (
            <span className="search-results-count-link">
                <Link to={`/job/${search.jobId}/search/${search.id}/${resultsType}`}>{value}</Link>
            </span>
        );
    };

    const renderAutoSendToggle = (search: Search): JSX.Element => {
        if (search.sortRank === BASELINE_SEARCH_SORT_RANK || search.status !== SearchStatus.Active) {
            return null;
        }
        const tooltip = search.reviewRequiredForOutreach
            ? 'Outreach from this search is held for manual review before sending'
            : 'New results from this search are sent outreach automatically';
        return <SearchAutoSendToggle search={search} job={job} tooltip={tooltip} onUpdateSearch={onUpdateSearch} />;
    };

    const getCellRenderer = {
        added: (value: CellValue, search: Search) => renderClickableCount(value, search, 'added'),
        alreadyAdded: (value: CellValue, search: Search) => renderClickableCount(value, search, 'already_added'),
        blocked: (value: CellValue, search: Search) => renderClickableCount(value, search, 'blocked'),
        created: (value: CellValue) => timeRelativeDay(value as number),
        createdBy: (value: CellValue) => <User id={value as string} />,
        expiredProfile: (value: CellValue, search: Search) => renderClickableCount(value, search, 'expired_profile'),
        findingEmail: (value: CellValue, search: Search) => renderClickableCount(value, search, 'finding_email'),
        inCooldown: (value: CellValue, search: Search) => renderClickableCount(value, search, 'in_cooldown'),
        lowScore: (value: CellValue, search: Search) => renderClickableCount(value, search, 'low_score'),
        noScore: (value: CellValue, search: Search) => renderClickableCount(value, search, 'no_score'),
        owner: (value: CellValue) => <User id={value as string} />,
        ready: (value: CellValue, search: Search) => renderClickableCount(value, search, 'ready'),
        rejected: (value: CellValue, search: Search) => renderClickableCount(value, search, 'rejected'),
        status: (value: CellValue, search: Search) => (
            <span className="search-status-with-auto-send">
                {value}
                {renderAutoSendToggle(search)}
            </span>
        ),
        title: (_: CellValue, search: Search) => (
            <JobSearchesTitle
                onCloneSearch={onCloneSearch}
                onUpdateSearch={onUpdateSearch}
                onDeleteSearch={onDeleteSearch}
                search={search}
                job={job}
            />
        ),
        totalNew: (value: CellValue, search: Search) => renderClickableCount(value, search, 'total_new')
    };

    // filter status on its plain label; the renderer wraps it in JSX for the auto-send toggle
    const statusFilterValue = (value: CellValue) => value;
    const cellFilterValues = { ...getCellRenderer, status: statusFilterValue };

    return (
        <DataPanel
            columns={columns}
            data={filteredSearches}
            getCellValue={getCellValue}
            getCellRenderer={getCellRenderer}
            getCellFilterValue={cellFilterValues}
            hidePagination={true}
            sortableColumns={sortableColumns}
            filterableColumns={filterableColumns}
            initialSortColumn="created"
            initialSortDirection="asc"
            pageSize={100}
            tableContainerRef={tableContainerRef}
            className="job-searches-data-panel"
        />
    );
};
